import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getReaderChapterVoice, ReaderChapter } from '@/services/booksApi';
import { fetchChapterRuntime } from '@/hooks/useChapterAudio';
import { ChatSessionSummary } from '@/types/chat';
import { ChapterRuntime } from '@/utils/ordinalSync';
import { getActiveReaderRoot, getMountedChapterIndex } from '@/utils/readerDomRegistry';
import {
  MarginDotPlacement,
  MarginLinePoint,
  layoutMarginDots,
  measureConversationLines,
} from '@/utils/conversationMarginDots';
import { formatRelative } from '@/utils/formatRelative';
import { AnalyticsInputMethod, inputMethodFromEvent } from '@/services/analyticsContext';

const DOT_PX = 12;
// Runtimes kept for chapters the reader has shown. At most five chapters are
// mounted at once, so this only bounds a long session of paging around.
const RUNTIME_KEEP_LIMIT = 8;

export type MarginDotVariant = 'full' | 'ring';

interface MarginDotProps {
  /**
   * 'full' marks a conversation. 'ring' is the outlined variant reserved for
   * the question-suggestion feature; nothing in the product renders it yet.
   */
  variant?: MarginDotVariant;
  /** The sheet currently shows this dot's conversation. */
  active?: boolean;
  isDarkMode: boolean;
  label: string;
  /** The dot's centre, px from the top of the layer. */
  top: number;
  /** The tap target, px from the top of the layer; it spans the layer's width. */
  hitTop: number;
  hitBottom: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const MarginDot: React.FC<MarginDotProps> = ({
  variant = 'full',
  active = false,
  isDarkMode,
  label,
  top,
  hitTop,
  hitBottom,
  onClick,
}) => {
  // Theme is branched here rather than with dark: variants, as the player's
  // other components do. Dark uses the hue of the dark word highlight.
  const fill = isDarkMode ? 'bg-[#5676FF]' : 'bg-blue-700';
  const ring = isDarkMode ? 'bg-[#1C1C1E] border-[#5676FF]' : 'bg-white border-blue-700';
  const halo = isDarkMode
    ? 'shadow-[0_0_0_5px_rgba(86,118,255,0.24)]'
    : 'shadow-[0_0_0_5px_rgba(0,0,255,0.18)]';
  return (
    <button
      type="button"
      className="group pointer-events-auto absolute inset-x-0 cursor-pointer rounded-full border-0 bg-transparent p-0"
      style={{ top: hitTop, height: hitBottom - hitTop }}
      aria-label={label}
      aria-current={active ? 'true' : undefined}
      data-testid="conversation-margin-dot"
      data-variant={variant}
      onClick={onClick}
    >
      <span
        aria-hidden="true"
        className={`absolute left-1/2 block h-3 w-3 -translate-x-1/2 rounded-full transition-transform duration-150 group-hover:scale-125 group-focus-visible:scale-125 motion-reduce:transition-none ${
          variant === 'ring' ? `border-[2.5px] ${ring}` : fill
        }${active ? ` ${halo}` : ''}`}
        style={{ top: top - hitTop - DOT_PX / 2 }}
      />
    </button>
  );
};

export interface ConversationMarginDotsProps {
  /** The book's anchored conversations; grouped by chapter here. */
  sessions: readonly ChatSessionSummary[];
  chapters: readonly ReaderChapter[];
  currentChapter: ReaderChapter | null;
  /** The current chapter's runtime, as the reader already holds it. */
  runtime: ChapterRuntime | null;
  selectedReadingVoiceKey: string;
  /** Advances when the selected chapter's document settles. */
  documentVersion: number;
  /** Advances on resize and orientation change. */
  layoutVersion: number;
  /** The reader column the dots are measured against and positioned in. */
  wrapperRef: React.RefObject<HTMLElement | null>;
  activeConversationId: number | null;
  isDarkMode: boolean;
  onOpenConversation: (session: ChatSessionSummary, inputMethod: AnalyticsInputMethod) => void;
}

const runtimeKey = (chapterId: string, voiceKey: string): string => `${chapterId}:${voiceKey}`;

const samePlacements = (a: MarginDotPlacement[], b: MarginDotPlacement[]): boolean =>
  a.length === b.length && a.every((placement, i) => {
    const other = b[i];
    return placement.top === other.top
      && placement.hitTop === other.hitTop
      && placement.hitBottom === other.hitBottom
      && placement.sessions.length === other.sessions.length
      && placement.sessions.every((session, j) => session === other.sessions[j]);
  });

/**
 * The margin dots for every mounted chapter. Lives outside the reader's
 * closed shadow root and measures through the registry's element references,
 * so theme tokens apply, tests can find the buttons, and a tap never reaches
 * the reader's delegated tap-to-seek handler.
 */
const ConversationMarginDots: React.FC<ConversationMarginDotsProps> = ({
  sessions,
  chapters,
  currentChapter,
  runtime,
  selectedReadingVoiceKey,
  documentVersion,
  layoutVersion,
  wrapperRef,
  activeConversationId,
  isDarkMode,
  onOpenConversation,
}) => {
  const sessionsByChapter = useMemo(() => {
    const byChapter = new Map<string, ChatSessionSummary[]>();
    for (const session of sessions) {
      const chapterSessions = byChapter.get(session.anchor_chapter_id);
      if (chapterSessions) chapterSessions.push(session);
      else byChapter.set(session.anchor_chapter_id, [session]);
    }
    return byChapter;
  }, [sessions]);
  const chaptersById = useMemo(
    () => new Map(chapters.map((chapter) => [chapter.id, chapter] as const)),
    [chapters],
  );

  const [placements, setPlacements] = useState<MarginDotPlacement[]>([]);
  // Each counter names one reason to measure again.
  const [measureVersion, setMeasureVersion] = useState(0);
  const [mountVersion, setMountVersion] = useState(0);
  const [runtimeVersion, setRuntimeVersion] = useState(0);
  const runtimesRef = useRef(new Map<string, ChapterRuntime>());
  const failedRuntimesRef = useRef(new Set<string>());
  const pendingRuntimesRef = useRef(new Map<string, AbortController>());

  const keepRuntime = useCallback((key: string, value: ChapterRuntime) => {
    const runtimes = runtimesRef.current;
    runtimes.delete(key);
    runtimes.set(key, value);
    while (runtimes.size > RUNTIME_KEEP_LIMIT) {
      const oldest = runtimes.keys().next().value;
      if (oldest === undefined) break;
      runtimes.delete(oldest);
    }
  }, []);

  // Neighbour chapters mount and unmount without advancing documentVersion
  // (only the selected chapter does, so background mounts don't re-run
  // positioning). The slots flip data-chapter-mounted instead; watch that.
  useEffect(() => {
    const readerRoot = getActiveReaderRoot();
    if (!readerRoot || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => setMountVersion((value) => value + 1));
    observer.observe(readerRoot, { attributes: true, attributeFilter: ['data-chapter-mounted'], subtree: true });
    return () => observer.disconnect();
  }, [documentVersion]);

  // Anything that changes the column's height moves lines below it: fonts
  // and images arriving, a chapter above the viewport swapping its placeholder
  // for content. The word-sync observer watches only the current chapter.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setMeasureVersion((value) => value + 1));
    observer.observe(wrapper);
    for (const chapterId of sessionsByChapter.keys()) {
      const index = getMountedChapterIndex(chapterId);
      if (index) observer.observe(index.root);
    }
    return () => observer.disconnect();
  }, [wrapperRef, sessionsByChapter, documentVersion, mountVersion]);

  // Runtimes for mounted chapters other than the current one.
  useEffect(() => {
    const voiceKey = selectedReadingVoiceKey;
    for (const chapterId of sessionsByChapter.keys()) {
      if (chapterId === currentChapter?.id || !getMountedChapterIndex(chapterId)) continue;
      const key = runtimeKey(chapterId, voiceKey);
      if (runtimesRef.current.has(key) || failedRuntimesRef.current.has(key) || pendingRuntimesRef.current.has(key)) {
        continue;
      }
      const chapter = chaptersById.get(chapterId);
      if (!chapter || !getReaderChapterVoice(chapter, voiceKey)?.wordSync.available) {
        // A chapter without timings has no word to place a dot on.
        failedRuntimesRef.current.add(key);
        continue;
      }
      const controller = new AbortController();
      pendingRuntimesRef.current.set(key, controller);
      fetchChapterRuntime(chapter, voiceKey, controller.signal)
        .then((loaded) => {
          keepRuntime(key, loaded);
          setRuntimeVersion((value) => value + 1);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          failedRuntimesRef.current.add(key);
          if (import.meta.env.DEV) {
            console.warn(`[margin dots] No runtime for chapter '${chapterId}'; its dots are skipped.`, error);
          }
        })
        .finally(() => {
          if (pendingRuntimesRef.current.get(key) === controller) pendingRuntimesRef.current.delete(key);
        });
    }
  }, [sessionsByChapter, chaptersById, currentChapter, selectedReadingVoiceKey, documentVersion, mountVersion, keepRuntime]);

  useEffect(() => () => {
    pendingRuntimesRef.current.forEach((controller) => controller.abort());
    pendingRuntimesRef.current.clear();
  }, []);

  useLayoutEffect(() => {
    // The reader hands us the current chapter's runtime; keep it so the
    // chapter's dots survive paging away from it without a refetch.
    if (runtime && currentChapter && runtime.tokenMap.chapterId === currentChapter.id) {
      const key = runtimeKey(currentChapter.id, selectedReadingVoiceKey);
      if (runtimesRef.current.get(key) !== runtime) keepRuntime(key, runtime);
    }
    const wrapper = wrapperRef.current;
    const points: MarginLinePoint[] = [];
    if (wrapper && sessionsByChapter.size > 0) {
      const wrapperTop = wrapper.getBoundingClientRect().top;
      for (const [chapterId, chapterSessions] of sessionsByChapter) {
        const index = getMountedChapterIndex(chapterId);
        const chapterRuntime = runtimesRef.current.get(runtimeKey(chapterId, selectedReadingVoiceKey));
        if (!index || !chapterRuntime) continue;
        points.push(...measureConversationLines(chapterSessions, chapterRuntime, index, wrapperTop));
      }
    }
    const next = layoutMarginDots(points);
    setPlacements((previous) => (samePlacements(previous, next) ? previous : next));
  }, [
    wrapperRef,
    sessionsByChapter,
    currentChapter,
    runtime,
    selectedReadingVoiceKey,
    documentVersion,
    layoutVersion,
    measureVersion,
    mountVersion,
    runtimeVersion,
    keepRuntime,
  ]);

  if (placements.length === 0) return null;

  return (
    // The layer is the margin itself: from the text's edge to the screen's
    // edge on a phone (32px, so the tap target is clipped there and never
    // sits on the last word), and a full 44px from md up, where the centred
    // column has free space beside it.
    <div
      className="pointer-events-none absolute inset-y-0 -right-4 w-8 md:-right-7 md:w-11"
      data-testid="conversation-margin-dots"
    >
      {placements.map((placement) => {
        const newest = placement.sessions[0];
        const count = placement.sessions.length;
        const when = formatRelative(newest.last_message_at);
        const label = count === 1
          ? `Conversation, ${when}`
          : `${count} conversations, latest ${when}`;
        const active = activeConversationId != null
          && placement.sessions.some((session) => session.id === activeConversationId);
        return (
          <MarginDot
            key={newest.id}
            isDarkMode={isDarkMode}
            label={label}
            top={placement.top}
            hitTop={placement.hitTop}
            hitBottom={placement.hitBottom}
            active={active}
            onClick={(event) => onOpenConversation(newest, inputMethodFromEvent(event))}
          />
        );
      })}
    </div>
  );
};

export default ConversationMarginDots;
